import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import Dropzone from 'react-dropzone';
import Modal from './../../modal';
import messages from '../../../text/messages';
import { uuid } from './../../../utilities/string';
import { createDocument, upload, setReplaceDocument, archiveDocument } from '../../../reducers/documents';
import Document from './../../../models/document';

import './../document.css';
import { Snackbar } from '../../snackbar';

class DocumentUploader extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            modal: {
                open: false,
                replaceDocument: undefined
            },
            files: [],
            showDropzone: false
        };

        this.onDrop = this.onDrop.bind(this);
        this.uploadFiles = this.uploadFiles.bind(this);
        this.attachAnother = this.attachAnother.bind(this);
        this.renderModal = this.renderModal.bind(this);
        this.updateFileProgress = this.updateFileProgress.bind(this);
        this.getRemainingDocumentsToBeUploadedCount = this.getRemainingDocumentsToBeUploadedCount.bind(this);

        // modal controls
        this.onOpen = this.onOpen.bind(this);
        this.onClose = this.onClose.bind(this);
    }

    attachAnother() {
        this.setState({ ...this.state, showDropzone: true });
    }

    uploadFiles(files) {
        const { role, docType, displayName, upload, createDocument, uploadDocument } = this.props;
        const { permissions, onUploaded } = upload;
        const { replaceDocument } = this.state.modal;
        files = files.slice(0, replaceDocument ? 1 : this.getRemainingDocumentsToBeUploadedCount());

        let filesData = files.map(f => ({ id: uuid(), file: f, progress: 0 }));
        this.setState({ ...this.state, files: [...this.state.files, ...filesData], showDropzone: false }, () => {
            // create document records
            filesData.forEach(data => {
                let document = new Document(data.file, role, docType, displayName, permissions);
                createDocument(document, result => {
                    // upload document
                    uploadDocument(
                        data.file,
                        result.s3_url,
                        () => {
                            this.updateFileProgress(data.id, 100);
                            Snackbar.success(<FormattedMessage {...{ ...messages.uploadSuccess, values: { type: displayName || docType } }} />);
                            if (onUploaded) {
                                onUploaded(document);
                            }
                        },
                        data.id,
                        this.updateFileProgress
                    );
                });
            });
        });
    }

    onDrop(files) {
        const { archiveDocument } = this.props;
        const { replaceDocument } = this.state.modal;

        if (replaceDocument) {
            archiveDocument(replaceDocument.id, () => this.uploadFiles(files));
            return;
        }

        this.uploadFiles(files);
    }

    getRemainingDocumentsToBeUploadedCount() {
        const { documents, upload } = this.props;

        let existingDocs = documents ? documents.length : 0;
        if (upload && upload.limit) {
            let limit = upload && upload.limit ? upload.limit : 1;
            return limit - existingDocs;
        }

        return 100;
    }

    updateFileProgress(id, progress) {
        const { getDocuments } = this.props;
        let files = this.state.files;
        if (files.filter(f => f.id === id)[0]) {
            files.filter(f => f.id === id)[0].progress = progress;
        }

        this.setState({ ...this.state, files: files, showDropzone: false }, () => {
            if (files.filter(f => f.progress !== 100).length === 0) {
                getDocuments(() => {
                    this.setState({ ...this.state, modal: { open: false }, files: [] });
                });
            }
        });
    }

    onOpen() {
        this.setState({ ...this.state, modal: { open: true } });
    }

    onClose() {
        this.setState({ ...this.state, modal: { open: false } });
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { id, replaceDocument, setReplaceDocument } = nextProps;
        if (!replaceDocument) {
            return;
        }

        this.setState({ ...this.state, modal: { open: true, replaceDocument: replaceDocument } }, () => setReplaceDocument(undefined, id));
    }

    render() {
        const { docType, displayName, upload } = this.props;
        const { modal } = this.state;

        // Do not allow more docs to be uploaded.
        if (this.getRemainingDocumentsToBeUploadedCount() < 1) {
            return modal.replaceDocument ? this.renderModal() : null;
        }

        let button =
            upload && upload.button ? (
                upload.button(this.onOpen, docType)
            ) : (
                <div className="attach-file-button" onClick={this.onOpen}>
                    <i className="material-icons">attach_file</i>
                    <span>
                        <FormattedMessage {...messages.attachFile} values={{ type: displayName || docType }} />
                    </span>
                </div>
            );

        return (
            <div className="document-manager-wrapper">
                <div className="row document-manager">
                    <div className="col-xs-12">
                        {button}
                        {this.renderModal()}
                    </div>
                </div>
            </div>
        );
    }

    renderModal() {
        const { modal, files, showDropzone } = this.state;
        const { upload, docType, displayName } = this.props;
        const { limit } = upload;

        let dropzoneVisible = files.length === 0 || showDropzone;

        return (
            <Modal open={modal.open} onClose={this.onClose} managedClose size="col-xs-12 col-sm-8 col-sm-offset-2">
                <div>
                    <div className="row upload-header">
                        <div className="col-xs-9 ellipsis">
                            <span className="upload-title">
                                <FormattedMessage {...messages.upload} /> {displayName || docType}
                            </span>
                        </div>
                        <div className="col-xs-1 pull-right">
                            <div className="upload-header-actions">
                                <i className="material-icons" onClick={this.onClose}>
                                    close
                                </i>
                            </div>
                        </div>
                    </div>

                    {!dropzoneVisible && (
                        <div className="row upload-progress">
                            {files.map((f, i) => (
                                <div className="col-xs-12 item" key={i}>
                                    <i className="hidden-xs col-sm-1 material-icons no-select">description</i>
                                    <div className="col-xs-12 col-sm-11">
                                        <span className="col-xs-12 file-name">{f.file.name}</span>
                                        <span className="col-xs-12 status">
                                            {f.progress === 100 ? <FormattedMessage {...messages.completed} /> : <FormattedMessage {...messages.uploading} />}
                                        </span>
                                        <div className="col-xs-12 progress-container">
                                            <div className="progress-value" style={{ width: `${f.progress}%` }} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {dropzoneVisible && (
                        <Dropzone multiple={(!limit || (limit && limit > 1)) && !modal.replaceDocument} className="dropzone" onDrop={this.onDrop}>
                            <div className="dropzone-content text-center">
                                <i className="material-icons">cloud_upload</i>
                                <div className="hidden-xs">
                                    <FormattedHTMLMessage {...messages.dragAndDrop} tagName={'div'} />
                                </div>
                                <div className="visible-xs">
                                    <FormattedHTMLMessage {...messages.attachFileOrPhoto} tagName={'div'} />
                                </div>
                            </div>
                        </Dropzone>
                    )}

                    {!dropzoneVisible && (
                        <div className="row">
                            <div className="col-xs-6">
                                {(!limit || (limit && limit > 1)) && (
                                    <span className="upload-button" onClick={this.attachAnother}>
                                        <FormattedMessage {...messages.attachAnother} />
                                    </span>
                                )}
                            </div>
                            <div className="col-xs-6">
                                <span className="upload-button pull-right" onClick={this.onClose}>
                                    <FormattedMessage {...messages.close} />
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
        );
    }
}

DocumentUploader.defaultProps = {
    id: undefined,
    role: undefined,
    docType: undefined,
    displayName: undefined,
    getDocuments: undefined
};
DocumentUploader.propTypes = {
    id: PropTypes.string.isRequired,
    role: PropTypes.object.isRequired,
    docType: PropTypes.string,
    displayName: PropTypes.any,
    getDocuments: PropTypes.func.isRequired
};

const mapStateToProps = (state, props) => ({
    documents: props.providedDocuments || (state.documents[props.id] ? state.documents[props.id].list : undefined),
    replaceDocument: state.documents[props.id] ? state.documents[props.id].replaceDocument : undefined
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            createDocument: createDocument,
            uploadDocument: upload,
            archiveDocument: archiveDocument,
            setReplaceDocument: setReplaceDocument
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(DocumentUploader);



// WEBPACK FOOTER //
// ./src/components/document/uploader/index.jsx